import { derivedStream, spreadMap } from '@ardoq/rxbeach';
import { context$ } from 'streams/context/context$';
import { type ModeViews, workspaceModeViews$ } from 'views/modeViews$';
import { deprecatedViews$ } from 'views/deprecatedViews$';
import { distinctUntilChanged, shareReplay, startWith } from 'rxjs/operators';
import {
  persistentWorkspaces$ as workspaces$,
  WorkspacesState,
} from 'streams/workspaces/workspaces$';
import { isEqual, uniq } from 'lodash';
import { MetaInfo } from 'streams/views/mainContent/types';
import {
  APICurrentUser,
  APIWorkspaceAttributes,
  ArdoqId,
  PersonalSetting,
  ViewIds,
} from '@ardoq/api-types';
import { ContextShape } from '@ardoq/data-model';
import { DEFAULT_REPLACEMENT_FOR_WORKSPACE_MODE } from 'views/environmentals/restrictedViews';
import { getRecentlyUsedStartView } from './startViewFallback';
import { getOpenedWorkspaceStartView } from './tabUtils';
import { focusedChrome$ } from 'scope/focusedChrome$';
import {
  type FocusedChrome,
  focusedChromeOperations,
} from 'scope/focusedChromeOperations';
import { isTruthy } from '@ardoq/common-helpers';
import { Observable } from 'rxjs';
import { featureOperations, Features, FeatureContext } from '@ardoq/features';
import { userOperations } from '@ardoq/core';
import { featureContext$ } from 'streams/featureContext$';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';

const defaultState: TabOptionsStreamShape = {
  openedWorkspaceStartView: null,
  activeWorkspaceId: '',
  metaInfoList: [],
  viewsAvailableInScope: new Set(),
  focusedChrome: focusedChromeOperations.NO_FOCUSED_CHROME,
  isRestrictedToQuickStartViews: false,
  isHardNaxEnabled: false,
};

export type TabOptionsStreamShape = {
  metaInfoList: MetaInfo[];
  openedWorkspaceStartView: ViewIds | null;
  activeWorkspaceId: ArdoqId;
  viewsAvailableInScope: Set<ViewIds>;
  focusedChrome: FocusedChrome;
  isRestrictedToQuickStartViews: boolean;
  isHardNaxEnabled: boolean;
};

const getAllAvailableViewsSavedInWorkspace = (
  workspaceAttributes: APIWorkspaceAttributes
) =>
  uniq(
    [workspaceAttributes.startView, ...workspaceAttributes.views].filter(
      isTruthy
    )
  );

export const toTabOptions = (
  {
    views: metaInfoList,
    ids: availableViewIds,
    byIds: workspaceModeViewsById,
  }: ModeViews<ViewIds>,
  { workspaceId, presentationId }: ContextShape,
  { byId }: WorkspacesState,
  featureContext: FeatureContext,
  currentUser: APICurrentUser,
  focusedChrome: FocusedChrome,
  deprecatedViews: MetaInfo[]
): TabOptionsStreamShape => {
  if (metaInfoList.length === 0 || !workspaceId) {
    return defaultState;
  }

  const defaultView = DEFAULT_REPLACEMENT_FOR_WORKSPACE_MODE;

  const workspaceAttributes = byId[workspaceId];

  const isFocusedChrome =
    focusedChromeOperations.isFocusedChrome(focusedChrome);

  const recentlyUsedViewStyle = featureOperations.hasFeature(
    featureContext,
    Features.RECENTLY_USED_VIEW_STYLES
  )
    ? (userOperations.getPersonalSetting(
        currentUser,
        PersonalSetting.RECENTLY_USED_VIEW_STYLE
      ) ?? null)
    : null;

  const openedWorkspaceStartView = getOpenedWorkspaceStartView({
    focusedChrome,
    hasContextPresentation: Boolean(presentationId),
    workspaceStartView: workspaceAttributes?.startView ?? undefined,
    recentlyUsedViewStyle,
    defaultView,
    availableViewIds,
    deprecatedViews,
    workspaceModeViewsById,
  });

  /**
   * Views saved in workspace may be discontinued, but if they are not restricted,
   * we still need them to be availabe in tabs to facilitate the manual migration.
   *
   * The openedWorkspaceStartView, however, should not be loaded if it is discontinued
   */
  const allowedViews = workspaceAttributes
    ? getAllAvailableViewsSavedInWorkspace(workspaceAttributes)
    : [];

  const isOpeningWorkspaceFromAssetBrowser =
    !isFocusedChrome && !presentationId;

  const recentlyUsedStartView = isOpeningWorkspaceFromAssetBrowser
    ? getRecentlyUsedStartView({
        workspaceStartView: workspaceAttributes?.startView ?? null,
        recentlyUsedViewStyle,
        workspaceModeViewsById,
      })
    : undefined;

  const viewsAvailableInScope = isFocusedChrome
    ? new Set(
        focusedChromeOperations.getAllowedViewIds(focusedChrome, defaultView)
      )
    : new Set(
        [...allowedViews, defaultView, recentlyUsedStartView].filter(isTruthy)
      );

  return {
    // The order of the tabs should follow the order of the views hardcoded in metaInfoTabs.
    metaInfoList,
    openedWorkspaceStartView,
    activeWorkspaceId: workspaceId,
    viewsAvailableInScope,
    focusedChrome,
    isRestrictedToQuickStartViews: featureOperations.hasFeature(
      featureContext,
      Features.QUICK_START_RESTRICT_AVAILABLE_VISUALIZATIONS
    ),
    isHardNaxEnabled: featureOperations.isHardNaxEnabled(featureContext),
  };
};

const tabOptions$: Observable<TabOptionsStreamShape> = derivedStream(
  'tabOptions$',
  workspaceModeViews$,
  context$,
  workspaces$,
  featureContext$,
  loadedCurrentUser$,
  focusedChrome$,
  deprecatedViews$
).pipe(
  spreadMap(toTabOptions),
  distinctUntilChanged((a, b) => isEqual(a, b)),
  startWith(defaultState),
  shareReplay({ bufferSize: 1, refCount: true })
);

export { tabOptions$ };
